import type { Consumer, Subscription } from '@rails/actioncable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  anchorFromWire,
  type ClockModel,
  type ClockState,
  readClock,
  setClock,
  startClock,
  stopClock,
} from '@/features/courts/clock/model';
import type { SportMatch, SportSpec, WireState } from '@/types/sports/wire';
import { getDeviceId } from '@/utils/device-id';
import { getSubdomain } from '@/utils/get-subdomain';
import { localNow } from '@/utils/local-now';

import { courtSnapshot } from './court-snapshot';
import { isTimeMessage, useServerClock } from './use-server-clock';

const PUSH_INTERVAL_MS = 5_000;
const RESAMPLE_INTERVAL_MS = 120_000;

export interface CourtMatch<TConfig, TState extends WireState> {
  match: SportMatch<TConfig, TState> | undefined;
  clocks: Record<string, ClockState>;
  clockModels: ClockModel[];
  send: (patch: Partial<TState>) => void;
  setClockValue: (name: string, seconds: number) => void;
  differentControlDevice: boolean;
  selectionRequired: boolean;
  takeControl: () => void;
  finalizeMatch: (matchId: string) => void;
  startNextMatch: (matchId: string) => void;
  serverNow: () => number | null;
}

interface Received<TMatch> {
  match: TMatch;
  receivedAt: number;
}

export const useCourtMatch = <TConfig, TState extends WireState>(
  courtId: string,
  cable: Consumer,
  spec: SportSpec<TConfig, TState>,
  code?: string,
): CourtMatch<TConfig, TState> => {
  type TMatch = SportMatch<TConfig, TState>;

  const snapshot = useMemo(() => courtSnapshot<TMatch>(), []);
  const serverClock = useServerClock();
  const serverClockRef = useRef(serverClock);
  serverClockRef.current = serverClock;

  const [match, setMatch] = useState<TMatch | undefined>(() =>
    code ? snapshot.get(courtId)?.match : undefined,
  );
  const [clocks, setClocks] = useState<Record<string, ClockState>>(
    () => (code ? snapshot.get(courtId)?.clocks : undefined) ?? {},
  );
  const [initialized, setInitialized] = useState(() =>
    Boolean(code && snapshot.get(courtId)),
  );
  const [awaitingMatchId, setAwaitingMatchId] = useState<string>();
  const [differentControlDevice, setDifferentControlDevice] = useState(false);
  const [selectionRequired, setSelectionRequired] = useState(false);
  const [lastReceived, setLastReceived] = useState<Received<TMatch>>();
  const channelRef = useRef<Subscription>();
  const stateRef = useRef({ match, clocks, initialized, awaitingMatchId });
  stateRef.current = { match, clocks, initialized, awaitingMatchId };

  const clockModels = useMemo(
    () => (match ? spec.clocks(match.config) : []),
    [match, spec],
  );

  const anchorAll = useCallback(
    (incoming: TMatch, receivedAt: number): Record<string, ClockState> => {
      const models = spec.clocks(incoming.config);
      const running = spec.runningStateOf(incoming.state);
      const syncedAt = incoming.state.timings?.synced_at;
      const { offset } = serverClockRef.current;
      const serverAtReceive = offset === null ? null : receivedAt + offset;
      const age =
        syncedAt !== undefined && serverAtReceive !== null
          ? (serverAtReceive - syncedAt) / 1000
          : 0;

      return Object.fromEntries(
        models.map((model) => [
          model.name,
          anchorFromWire(
            incoming.state.timings?.[model.name] ?? 0,
            model.direction,
            model.runsIn.includes(running),
            age,
            localNow(),
          ),
        ]),
      );
    },
    [spec],
  );

  const applySnapshot = useCallback(
    (incoming: TMatch) => {
      const receivedAt = localNow();

      setMatch(incoming);
      setClocks(anchorAll(incoming, receivedAt));
      setLastReceived({ match: incoming, receivedAt });
    },
    [anchorAll],
  );

  // A display that received its snapshot before the offset was known re-ages it
  // once the offset arrives.
  useEffect(() => {
    if (code || serverClock.offset === null || !lastReceived) {
      return;
    }

    setClocks(anchorAll(lastReceived.match, lastReceived.receivedAt));
    setLastReceived(undefined);
  }, [anchorAll, code, lastReceived, serverClock.offset]);

  useEffect(() => {
    const channel = cable.subscriptions.create(
      {
        channel: 'Scoreboard::CourtChannel',
        court_id: courtId,
        subdomain: getSubdomain(),
        ...(code && { code }),
      },
      {
        connected: () =>
          serverClockRef.current.sample((action, data) =>
            channel.perform(action, data),
          ),
        received: (data: unknown) => {
          if (isTimeMessage(data)) {
            serverClockRef.current.receive(data);
            return;
          }

          const current = stateRef.current;

          if (code) {
            const incoming = data as
              | (TMatch & { active_device_id?: string })
              | null;

            if (incoming && incoming.active_device_id !== getDeviceId()) {
              setDifferentControlDevice(true);
              return;
            }

            if (incoming) {
              setDifferentControlDevice(false);
            }

            if (current.initialized && !current.awaitingMatchId) {
              return;
            }

            if (
              incoming &&
              current.awaitingMatchId &&
              incoming.id !== current.awaitingMatchId
            ) {
              return;
            }

            setInitialized(true);
            setAwaitingMatchId(undefined);
          }

          if (!data) {
            setSelectionRequired(true);
            return;
          }

          setSelectionRequired(false);
          applySnapshot(data as TMatch);
        },
      },
    );

    channelRef.current = channel;
    serverClockRef.current.sample((action, data) =>
      channel.perform(action, data),
    );

    const resample = setInterval(
      () =>
        serverClockRef.current.sample((action, data) =>
          channel.perform(action, data),
        ),
      RESAMPLE_INTERVAL_MS,
    );

    return () => {
      clearInterval(resample);
      channel.unsubscribe();
      channelRef.current = undefined;
    };
  }, [applySnapshot, cable, code, courtId]);

  const wireTimings = useCallback(
    (models: ClockModel[], current: Record<string, ClockState>) =>
      Object.fromEntries(
        models.map((model) => [
          model.name,
          readClock(current[model.name], model.direction, localNow()),
        ]),
      ),
    [],
  );

  const push = useCallback(
    (nextMatch: TMatch, nextClocks: Record<string, ClockState>) => {
      const channel = channelRef.current;
      const models = spec.clocks(nextMatch.config);

      snapshot.set(courtId, { match: nextMatch, clocks: nextClocks });

      if (!channel || !code) {
        return;
      }

      const { synced_at: _omitted, ...timings } = nextMatch.state.timings ?? {};

      channel.perform('update', {
        match_id: nextMatch.id,
        device_id: getDeviceId(),
        controller_code: code,
        timestamp: serverClockRef.current.serverNow(),
        match: {
          ...nextMatch.state,
          ...(models.length > 0 && {
            timings: { ...timings, ...wireTimings(models, nextClocks) },
          }),
        },
      });
    },
    [code, courtId, snapshot, spec, wireTimings],
  );

  const send = useCallback(
    (patch: Partial<TState>) => {
      const current = stateRef.current;

      if (!current.match) {
        return;
      }

      const before = current.match.state;
      const after = { ...before, ...patch } as TState;
      const models = spec.clocks(current.match.config);
      const ranIn = spec.runningStateOf(before);
      const runsIn = spec.runningStateOf(after);
      const now = localNow();

      const nextClocks = Object.fromEntries(
        models.map((model) => {
          const clock = current.clocks[model.name] ?? {
            valueAtAnchor: 0,
            anchorAt: null,
          };
          const ran = model.runsIn.includes(ranIn);
          const runs = model.runsIn.includes(runsIn);

          if (runs && !ran) {
            return [model.name, startClock(clock, now)];
          }

          if (!runs && ran) {
            return [model.name, stopClock(clock, model.direction, now)];
          }

          return [model.name, clock];
        }),
      );
      const nextMatch = { ...current.match, state: after };

      // The ref is refreshed on render, so a clock set in the same tick as
      // this patch would otherwise push the pre-patch state back to the server.
      stateRef.current = { ...current, match: nextMatch, clocks: nextClocks };
      setMatch(nextMatch);
      setClocks(nextClocks);
      push(nextMatch, nextClocks);
    },
    [push, spec],
  );

  const setClockValue = useCallback(
    (name: string, seconds: number) => {
      const current = stateRef.current;

      if (!current.match || !current.clocks[name]) {
        return;
      }

      const nextClocks = {
        ...current.clocks,
        [name]: setClock(current.clocks[name], seconds, localNow()),
      };

      stateRef.current = { ...current, clocks: nextClocks };
      setClocks(nextClocks);
      push(current.match, nextClocks);
    },
    [push],
  );

  useEffect(() => {
    if (!code || !initialized) {
      return;
    }

    const interval = setInterval(() => {
      const current = stateRef.current;

      if (current.match) {
        push(current.match, current.clocks);
      }
    }, PUSH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [code, initialized, push]);

  const takeControl = useCallback(() => {
    channelRef.current?.perform('take_control', {
      court_id: courtId,
      code,
      device_id: getDeviceId(),
    });
  }, [code, courtId]);

  const finalizeMatch = useCallback(
    (matchId: string) => {
      channelRef.current?.perform('finalize_match', {
        code,
        device_id: getDeviceId(),
        match_id: matchId,
      });
    },
    [code],
  );

  const startNextMatch = useCallback(
    (matchId: string) => {
      const current = stateRef.current;

      if (current.match) {
        push(current.match, current.clocks);
      }

      snapshot.set(courtId);
      setMatch(undefined);
      setClocks({});
      setAwaitingMatchId(matchId);
      channelRef.current?.perform('next_match', {
        code,
        device_id: getDeviceId(),
        match_id: matchId,
      });
    },
    [code, courtId, push, snapshot],
  );

  return {
    match,
    clocks,
    clockModels,
    send,
    setClockValue,
    differentControlDevice,
    selectionRequired,
    takeControl,
    finalizeMatch,
    startNextMatch,
    serverNow: serverClock.serverNow,
  };
};
